import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSite } from '../../context/SiteContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { addNovolexLogo } from '../../utils/pdfBranding';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const Reporter = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { getCurrentSiteId, currentSite } = useSite();
  const [lines, setLines] = useState([]);
  // Multi-línea: lista de líneas seleccionadas. Vacío => todas
  const [selectedLines, setSelectedLines] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  // Estructura: { line_code|"ALL": { summary:{...}, by_date:[], pareto:[] } }
  const [lineReports, setLineReports] = useState({});
  const reportRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated()) return;
    fetchLines();
  }, [isAuthenticated]);

  // Recargar líneas cuando cambia el sitio
  useEffect(() => {
    if (currentSite) {
      fetchLines();
      // Limpiar reportes al cambiar de sitio
      setLineReports({});
      setSelectedLines([]);
    }
  }, [currentSite]);

  const fetchLines = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Filtrar líneas por site_id
      const params = {};
      const siteId = getCurrentSiteId();
      if (siteId) {
        params.site_id = siteId;
      }
      
      const { data } = await axios.get('/catalogs/all', { headers, params });
      setLines(data.lines || []);
      if ((data.lines || []).length > 0) {
        // Por defecto: todas las líneas
        setSelectedLines((data.lines || []).map(l => l.code));
      }
    } catch (err) {
      console.error('Error fetching lines:', err);
    }
  };

  const fetchLineReport = async (lineCode) => {
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = {
      date_from: startDate.toISOString().slice(0,10),
      date_to: endDate.toISOString().slice(0,10),
    };
    
    // Filtrar por site_id
    const siteId = getCurrentSiteId();
    if (siteId) {
      params.site_id = siteId;
    }
    
    if (lineCode !== 'ALL') params.lines = lineCode;
    const [resSummary, resTrend, resPareto] = await Promise.all([
      axios.get('/analytics/summary', { headers, params }).then(r => r.data).catch(() => null),
      axios.get('/analytics/trend', { headers, params }).then(r => r.data).catch(() => []),
      axios.get('/analytics/pareto', { headers, params }).then(r => r.data).catch(() => [])
    ]);
    return {
      summary: resSummary || {},
      by_date: Array.isArray(resTrend) ? resTrend : [],
      pareto: Array.isArray(resPareto) ? resPareto : []
    };
  };

  const generateReports = async () => {
    try {
      setLoading(true);
      const targets = selectedLines.length ? selectedLines : lines.map(l=>l.code);
      const entries = await Promise.all(targets.map(async (code) => [code, await fetchLineReport(code)]));
      const map = entries.reduce((acc,[code,data])=>{ acc[code]=data; return acc; }, {});
      setLineReports(map);
    } catch (e) {
      console.error('Error generating reports', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const sectionNodes = Array.from(reportRef.current.querySelectorAll('.report-section[data-has-data="true"]'));
    if (!sectionNodes.length) return;

    const margin = 10; // mm
    const contentTop = 27; // reserva el encabezado para el logotipo
    const gap = 5; // vertical gap entre secciones
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - contentTop - margin;

    // Render previo de todos los canvases (mantiene calidad al escalarlos luego)
    const canvases = [];
    for (let i = 0; i < sectionNodes.length; i++) {
      const canvas = await html2canvas(sectionNodes[i], { scale: 2 });
      canvases.push(canvas);
    }

    // Agrupar en chunks de máximo 3
    for (let start = 0; start < canvases.length; start += 3) {
      if (start > 0) pdf.addPage();
      addNovolexLogo(pdf, { x: margin, y: 7, width: 30 });
      const group = canvases.slice(start, start + 3);

      // Calcular dimensiones iniciales (ajuste por ancho)
      const dims = group.map(cv => {
        const ratio = cv.height / cv.width; // alto/ancho
        const targetHeight = ratio * usableWidth; // altura necesaria si usamos todo el ancho
        return { canvas: cv, width: usableWidth, height: targetHeight };
      });
      const totalHeight = dims.reduce((s, d) => s + d.height, 0);
      const totalGaps = gap * (group.length - 1);
      let scale = 1;
      if (totalHeight + totalGaps > usableHeight) {
        scale = (usableHeight - totalGaps) / totalHeight;
      }

      let y = contentTop;
      dims.forEach(dim => {
        const w = dim.width * scale;
        const h = dim.height * scale;
        const imgData = dim.canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', margin, y, w, h);
        y += h + gap;
      });
    }

    pdf.save(`reporte_multilineas_${startDate.toISOString().slice(0,10)}_${endDate.toISOString().slice(0,10)}.pdf`);
  };

  const weeklyChartData = (report) => {
    if (!report || !report.by_date) return null;
    const labels = report.by_date.map(r=>r.date);
    const data = report.by_date.map(r=>r.total_quantity);
    return { labels, datasets: [{ label:'Total defectos', data, backgroundColor:'#0d6efd' }] };
  };

  const allSelectedLines = useMemo(()=> selectedLines.length ? selectedLines : lines.map(l=>l.code), [selectedLines, lines]);
  // Filtra líneas sin datos
  const linesWithData = useMemo(()=> {
    return allSelectedLines.filter(code => {
      const rep = lineReports[code];
      if (!rep) return false;
      const total = Number(rep?.summary?.total_quantity) || 0;
      if (total > 0) return true;
      if ((rep.by_date||[]).some(r => (Number(r.total_quantity)||0) > 0)) return true;
      return false;
    });
  }, [allSelectedLines, lineReports]);
  const toggleLineSelection = (code) => {
    setSelectedLines(prev => prev.includes(code) ? prev.filter(c=>c!==code) : [...prev, code]);
  };
  const selectAll = () => setSelectedLines(lines.map(l=>l.code));
  const clearAll = () => setSelectedLines([]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-extrabold text-blue-900">
          {t('reporter.title')} <span className="text-base font-normal text-gray-500">{t('reporter.subtitle')}</span>
        </h1>
        {currentSite && (
          <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold border-2 border-blue-300">
            📍 {currentSite.code} - {currentSite.name}
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('reporter.dateFrom')}</label>
            <DatePicker selected={startDate} onChange={(d) => setStartDate(d)} dateFormat="yyyy-MM-dd" className="rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('reporter.dateTo')}</label>
            <DatePicker selected={endDate} onChange={(d) => setEndDate(d)} dateFormat="yyyy-MM-dd" className="rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="min-w-[220px]">
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('reporter.selectLines')}</label>
            <div className="border rounded-lg p-2 h-40 overflow-auto bg-white space-y-1">
              <div className="flex gap-2 mb-1 text-xs">
                <button type="button" onClick={selectAll} className="px-2 py-0.5 bg-gray-100 rounded border text-gray-700 hover:bg-gray-200">{t('reporter.selectAll')}</button>
                <button type="button" onClick={clearAll} className="px-2 py-0.5 bg-gray-100 rounded border text-gray-700 hover:bg-gray-200">{t('reporter.clear')}</button>
              </div>
              {lines.map(l => (
                <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-50 rounded px-1 py-0.5">
                  <input type="checkbox" className="accent-blue-600" checked={allSelectedLines.includes(l.code)} onChange={()=>toggleLineSelection(l.code)} />
                  <span className="text-gray-800">{l.name} ({l.code})</span>
                </label>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-1">{t('reporter.emptyAll')}</div>
          </div>
          <div>
            <button className="px-4 py-2 bg-blue-700 text-white font-bold rounded-lg shadow hover:bg-blue-800 transition disabled:opacity-60" onClick={generateReports} disabled={loading}>
              {loading ? t('reporter.generating') : t('reporter.generate')}
            </button>
          </div>
          <div>
            <button className="px-4 py-2 bg-green-50 text-green-700 border border-green-300 rounded-lg font-semibold hover:bg-green-100 transition disabled:opacity-60" onClick={handleDownloadPDF} disabled={!Object.keys(lineReports).length}>
              {t('reporter.downloadPDF')}
            </button>
          </div>
        </div>
      </div>
      <div ref={reportRef} className="space-y-16 print:space-y-0">
        {linesWithData.map(code => {
          const rep = lineReports[code];
          const chartData = weeklyChartData(rep);
          const hasData = !!rep && ((Number(rep?.summary?.total_quantity)||0) > 0 || (rep?.by_date||[]).some(r => (Number(r.total_quantity)||0) > 0));
          return (
            <div key={code} data-has-data={hasData?'true':'false'} className="bg-white rounded-2xl shadow p-6 border border-gray-200 report-section print:shadow-none print:border-0 print:rounded-none" style={{ color:'#000', pageBreakAfter:'always' }}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-blue-800 mb-1">{t('reporter.summary')} {code}</h2>
                <div className="text-gray-700 mb-2">{t('reporter.range')}: {startDate.toISOString().slice(0,10)} — {endDate.toISOString().slice(0,10)}</div>
              </div>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-2/3">
                  <div className="bg-blue-50 rounded-xl shadow p-4 mb-6 print:shadow-none print:bg-white">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">{t('reporter.defectsByDay')}</h3>
                    {hasData && chartData ? <Bar data={chartData} /> : <div className="text-gray-500 italic">{t('reporter.noData')}</div>}
                  </div>
                </div>
                <div className="md:w-1/3 flex flex-col gap-6">
                  <div className="bg-green-50 rounded-xl shadow p-4 print:shadow-none print:bg-white">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">{t('reporter.summaryBox')}</h3>
                    <div className="text-gray-800">{t('reporter.totalDefects')}: <span className="font-bold text-blue-900">{hasData ? (rep?.summary?.total_quantity || 0) : '-'}</span></div>
                    <div className="text-gray-800">{t('reporter.uniqueProducts')}: <span className="font-bold text-blue-900">{hasData ? (rep?.summary?.unique_products || 0) : '-'}</span></div>
                    <div className="text-gray-800">{t('reporter.uniqueLines')}: <span className="font-bold text-blue-900">{hasData ? (rep?.summary?.unique_lines || 0) : '-'}</span></div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl shadow p-4 print:shadow-none print:bg-white">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">{t('reporter.topDefects')}</h3>
                    {hasData ? (
                      <ol className="list-decimal list-inside text-gray-800">
                        {(rep?.pareto || []).slice(0,5).map(p => (
                          <li key={p.defect_type}><span className="font-bold text-blue-900">{p.defect_type}</span>: {p.total_quantity}</li>
                        ))}
                      </ol>
                    ) : <div className="text-gray-500 italic">{t('reporter.noData')}</div>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {linesWithData.length === 0 && (
          <div className="bg-white rounded-2xl shadow p-8 text-center text-gray-600 border border-gray-200">{t('reporter.noDataFound')}</div>
        )}
      </div>
    </div>
  );
};

export default Reporter;
